import { useCallback, useState } from "react";

import { searchStatusActionLabelOf } from "components/SearchStatusConfirmModal";
import useStopSearchProperty from "hooks/api/useStopSearchProperty";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";

/**
 * Switches the search status of the order through the confirmation modal shared by the estimate and reject screens.
 *
 * @param order order whose search is stopped or resumed
 * @param reloadOrder refetches the order so the switch shows the new status
 * @param onSwitched called with the completion message once the status has changed
 * @returns whether the confirmation modal is open, how to open and close it, and the confirm handler
 * @example
 * const { openConfirmModal } = useOrderSearchStatusSwitch(order, mutate, showCompletion);
 */
const useOrderSearchStatusSwitch = (
  order: Order | undefined,
  reloadOrder: () => Promise<unknown>,
  onSwitched: (message: string) => void
) => {
  const { stopSearchProperty } = useStopSearchProperty();
  const { showErrorModal } = useErrorModalStatusContext();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const actionLabel = searchStatusActionLabelOf(order?.searchStatus);

  const switchSearchStatus = useCallback(async () => {
    if (!order?.id) return;
    const status = await stopSearchProperty(order.id).catch(() => undefined);
    setIsConfirmModalOpen(false);
    if (status !== 200) {
      showErrorModal({ httpMethod: "put" });
      return;
    }
    await reloadOrder();
    onSwitched(`検索を${actionLabel}しました`);
  }, [order, actionLabel, stopSearchProperty, reloadOrder, onSwitched, showErrorModal]);

  return {
    isConfirmModalOpen,
    openConfirmModal: () => setIsConfirmModalOpen(true),
    closeConfirmModal: () => setIsConfirmModalOpen(false),
    switchSearchStatus,
  };
};

export default useOrderSearchStatusSwitch;
